import { useState } from 'react';
import { useLocation } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import { EmptyState } from '@/components/EmptyState';
import Button from '@/design_system/Button';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import { useShowToast } from '@/design_system/Toast';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import IconCheck from '@/icons/Check.svg';
import IconCross from '@/icons/Cross.svg';
import IconEdit from '@/icons/Edit.svg';
import IconTrash from '@/icons/Trash.svg';
import { useBrands } from '@/models/brand';
import { useDefectLevelValues } from '@/models/defectLevelValue';
import { useDefectTypesOrganization, useDeleteDefectType } from '@/models/defectType';
import { type LegacySettingsLocationState } from '@/routes/SettingsBeta/legacy-settings';
import { useCurrentOrganization } from '@/services/auth';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';

import { ProductAttributeValuesHierarchy } from '../shared/ProductAttributeValuesHierarchy';

import { DefectDrawer } from './DefectDrawer';

export type DefectType = Endpoints['GET /defect-types/organization']['response'][number];

export type IDefectLevelValue = NonNullable<DefectType['defectLevelValue']>;
export type IDefectLevelValues = Endpoints['GET /defect-level-values']['response'];

export type DefectBrandValue = DefectType['brandToDefectTypeOrganizations'][number];

export const DefectsTable = () => {
  const { t } = useLingui();

  const { data: defects, isLoading } = useDefectTypesOrganization();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [defectTypeToEdit, setDefectTypeToEdit] = useState<DefectType | null>(null);

  // Settings beta links to a defect type by opening its drawer here, until defects move to the beta
  const { state } = useLocation();
  const defectTypeIdFromBeta = (state as LegacySettingsLocationState | null)?.defectTypeId;
  const [openedDefectTypeIdFromBeta, setOpenedDefectTypeIdFromBeta] = useState<string>();
  const defectTypeFromBeta = defects?.find(({ id }) => id === defectTypeIdFromBeta);

  if (defectTypeFromBeta && openedDefectTypeIdFromBeta !== defectTypeFromBeta.id) {
    setOpenedDefectTypeIdFromBeta(defectTypeFromBeta.id);
    setDefectTypeToEdit(defectTypeFromBeta);
    setIsDrawerOpen(true);
  }

  const columnWidths = {
    name: 'minmax(320px, auto)',
    productCategories: 'minmax(300px, auto)',
    defectLevelValue: 'minmax(300px, auto)',
    quantifiable: 'auto',
    linkedActions: 'auto',
    brands: 'auto',
    editOrDelete: 'auto',
  };

  return (
    <Stack gap="1.5rem" padding="0 0 24px 0">
      <Stack row alignItems="center" justifyContent="space-between" gap="8px">
        <Title size="200" as="h2">
          <Trans id="settings.defects.defects-table.title">Defects</Trans>
        </Title>
        <Button onPress={() => setIsDrawerOpen(true)}>
          <IconAdd />
          <Trans id="settings.defects.defects-table.add">New defect</Trans>
        </Button>
      </Stack>
      {!isLoading && !defects?.length ? (
        <EmptyState subtitle={<Trans id="settings.defects.defects-table.none">No defects</Trans>} />
      ) : (
        <Table
          ariaLabel={t({ id: 'settings.defects.defects-table.table.label', message: 'Defects' })}
          columnWidths={Object.values(columnWidths)}
          variant="grid"
          bordered
        >
          <Header>
            <Row>
              <Column>
                <Trans id="settings.defects.defects-table.column.name">Name</Trans>
              </Column>

              <Column>
                <Trans id="settings.defects.defects-table.column.product-categories">
                  Product categories
                </Trans>
              </Column>

              <Column>
                <Trans id="settings.defects.defects-table.column.defect-level-values">
                  Defect levels
                </Trans>
              </Column>

              <Column justifyContent="center">
                <Trans id="settings.defects.defects-table.column.isQuantifiable">
                  Quantifiable
                </Trans>
              </Column>

              <Column justifyContent="center">
                <Trans id="settings.defects.defects-table.column.actions">Linked actions</Trans>
              </Column>

              <Column justifyContent="center">
                <Trans id="settings.defects.defects-table.column.brands">Brands</Trans>
              </Column>

              <Column
                ariaLabel={t({
                  id: 'settings.defects.defects-table.column.edit-or-delete',
                  message: 'Edit or delete',
                })}
              />
            </Row>
          </Header>

          <Body>
            {defects?.map((defectType) => (
              <DefectTypesRow
                key={defectType.id}
                defectType={defectType}
                onEdit={() => {
                  setDefectTypeToEdit(defectType);
                  setIsDrawerOpen(true);
                }}
              />
            ))}
          </Body>
        </Table>
      )}

      <DefectDrawer
        defectType={defectTypeToEdit}
        onClose={() => {
          setIsDrawerOpen(false);

          // Wait for the Drawer CSS exit animation to finish
          setTimeout(() => {
            setDefectTypeToEdit(null);
          }, 150);
        }}
        isOpen={isDrawerOpen}
      />
    </Stack>
  );
};

const DefectTypesRow = ({ defectType, onEdit }: { defectType: DefectType; onEdit: () => void }) => {
  const showToast = useShowToast();

  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const [organization] = useCurrentOrganization();
  const { data: defectLevelValueOptions } = useDefectLevelValues({
    organizationId: organization?.id,
  });
  const [deleteConfirmationOpened, setDeleteConfirmationOpened] = useState(false);
  const { mutateAsync: deleteDefectType, isPending: isDeleteDefectTypePending } =
    useDeleteDefectType();

  const { data } = useBrands({
    organizationId: organization?.id,
    limit: 500,
    offset: 0,
  });
  const brands = data?.brands ?? [];

  const actionTypeNames = (
    <Stack>
      {defectType.actionTypeToDefectTypes.map(({ actionTypeOrganization }) => (
        <span key={actionTypeOrganization.id}>· {_db(actionTypeOrganization.name)}</span>
      ))}
    </Stack>
  );

  const brandsNames = (
    <Stack>
      {defectType.brandToDefectTypeOrganizations.map((brandToDefectTypeOrganization) => {
        const name = brands.find(({ id }) => id === brandToDefectTypeOrganization.brandId)?.name;

        return <span key={brandToDefectTypeOrganization.id}>· {name}</span>;
      })}
    </Stack>
  );
  const defectTypeName = _db(defectType.name);

  return (
    <>
      <Row>
        <Cell>
          <Stack>
            <InlineText>{defectTypeName}</InlineText>
            <InlineText size="200" color="disabled">
              {defectType.id}
            </InlineText>
          </Stack>
        </Cell>

        <Cell>
          <ProductAttributeValuesHierarchy
            productAttributeValues={defectType.productAttributeValues}
          />
        </Cell>

        <Cell>
          <InlineText>
            {defectType.defectLevelValue && defectLevelValueOptions?.length
              ? getDefectLevelValuesLabels(
                  defectType.defectLevelValue,
                  defectLevelValueOptions,
                  _db
                )
              : '-'}
          </InlineText>
        </Cell>

        <Cell justifyContent="center">
          {defectType.isQuantifiable ? <IconCheck /> : <IconCross />}
        </Cell>

        <Cell justifyContent="center">
          <Tooltip
            content={
              defectType.actionTypeToDefectTypes.length
                ? actionTypeNames
                : t({
                    id: 'settings.defects.defects-table.actions.no-actions',
                    message: 'No actions linked',
                  })
            }
            textAlign="start"
          >
            <Button style={{ padding: '1rem' }} variant="style-less">
              {defectType.actionTypeToDefectTypes.length}
            </Button>
          </Tooltip>
        </Cell>

        <Cell justifyContent="center">
          <Tooltip
            content={
              defectType.brandToDefectTypeOrganizations.length
                ? brandsNames
                : t({
                    id: 'settings.defects.defects-table.actions.no-brands',
                    message: 'No brands linked',
                  })
            }
            textAlign="start"
          >
            <Button style={{ padding: '1rem' }} variant="style-less">
              {defectType.brandToDefectTypeOrganizations.length}
            </Button>
          </Tooltip>
        </Cell>

        <Cell justifyContent="center">
          <Stack row gap="0.5rem" flexWrap="nowrap">
            <Button
              variant="secondary"
              iconOnly
              size="small"
              ariaLabel={t({ id: 'settings.defects.defects-table.edit', message: 'Edit' })}
              onPress={onEdit}
            >
              <IconEdit />
            </Button>
            <Button
              variant="danger"
              iconOnly
              size="small"
              ariaLabel={t({ id: 'settings.defects.defects-table.delete', message: 'Delete' })}
              onPress={() => setDeleteConfirmationOpened(true)}
            >
              <IconTrash />
            </Button>
          </Stack>
        </Cell>
      </Row>

      <ConfirmationDialog
        title={
          <Trans id="settings.defects.defects-table.delete.confirmation.title">Delete defect</Trans>
        }
        confirm={
          <Trans id="settings.defects.defects-table.delete.confirmation.confirm">Delete</Trans>
        }
        onConfirm={() =>
          deleteDefectType(defectType.id)
            .then(() => {
              showToast({
                text: (
                  <Trans id="settings.defects.defects-table.delete.confirmation.success">
                    The defect <b>&quot;{defectTypeName}&quot;</b> has been deleted.
                  </Trans>
                ),
                type: 'success',
              });
            })
            .catch(() => {
              showToast({
                text: (
                  <Trans id="settings.defects.defects-table.delete.confirmation.error">
                    Error while deleting the defect <b>&quot;{defectTypeName}&quot;</b>. Please try
                    again later.
                  </Trans>
                ),
                type: 'error',
              });
            })
        }
        showDialog={deleteConfirmationOpened}
        setShowDialog={setDeleteConfirmationOpened}
        maxWidth="35rem"
        isPending={isDeleteDefectTypePending}
      >
        <Stack gap="0.5rem">
          <p>
            <Trans id="settings.defects.defects-table.delete.confirmation.description.1">
              Are you sure you want to delete the defect <b>&quot;{defectTypeName}&quot;</b>?
            </Trans>
          </p>
          <p>
            <Trans id="settings.defects.defects-table.delete.confirmation.description.2">
              Active requests with this defect will continue to show it, but users won't be able to
              choose this defect for new requests.
            </Trans>
          </p>
          <hr />
          <p>
            <b>
              <Trans id="settings.defects.defects-table.delete.confirmation.description.3">
                This deletion cannot be undone.
              </Trans>
            </b>
          </p>
        </Stack>
      </ConfirmationDialog>
    </>
  );
};

/**
 *
 * @returns The tree of defect values for a given defect.
 */
export const getDefectLevelValuesTree = (
  lastDefectLevelValueOfTree: IDefectLevelValue,
  allDefectLevelValues: IDefectLevelValues
) => {
  const defectLevelValues = [lastDefectLevelValueOfTree];

  /**
   * Recursive function to get all the parents of a same tree
   */
  const addParent = (defectLevelValue: IDefectLevelValue) => {
    if (defectLevelValue.parentId) {
      const parentDefectLevelValue = allDefectLevelValues.find(
        (_defectLevelValue) => _defectLevelValue.id === defectLevelValue.parentId
      );

      if (!parentDefectLevelValue) {
        throw new Error(`Parent defect level value ${defectLevelValue.parentId} not found`);
      }

      defectLevelValues.push(parentDefectLevelValue);

      if (parentDefectLevelValue.parentId) {
        addParent(parentDefectLevelValue);
      }
    }
  };

  addParent(lastDefectLevelValueOfTree);

  return defectLevelValues.reverse();
};

/**
 *
 * @returns The list of defect values labels for a given defect.
 * Example: "Button > Snap > Double-aspect"
 */
const getDefectLevelValuesLabels = (
  lastDefectLevelValueOfTree: IDefectLevelValue,
  defectLevelValues: IDefectLevelValues,
  _db: (translation: I18nWording) => string
) => {
  const defectLevelValuesTree = getDefectLevelValuesTree(
    lastDefectLevelValueOfTree,
    defectLevelValues
  );

  return defectLevelValuesTree.map((defectLevelValue) => _db(defectLevelValue.name)).join(' > ');
};
